import { isIndependentlyDrivable } from "@/shared/components/visualization/Panel/ThreeDPanel/ModelLoader";
import type { FieldArity } from "@/shared/domain/urdf";
import type {
  Joint,
  JointBinding,
} from "@/shared/state/visualization/schema/v1/three-d";

/**
 * The sibling array a `sensor_msgs/JointState` publisher names its elements in.
 *
 * Used by {@link bindAllJoints}, which is the only thing that still authors the
 * name-indexed form. `JointState` correlates `position[i]` with `name[i]`, and
 * every ROS publisher of it spells that array `name`.
 */
export const JOINT_STATE_NAME_PATH = "name";

/** The joints one topic's data may drive: one degree of freedom, not coupled. */
export function bindableJoints(joints: readonly Joint[]): Joint[] {
  return joints.filter(isIndependentlyDrivable);
}

/**
 * The declarations a loaded model already carries, keyed by joint name.
 *
 * The system of record for a binding is the generated glTF's `ROBOTO_device`
 * extension, and this is how the editor reads it back: `readJointsFromArticulation`
 * has already copied each joint's `binding` onto `defaultBinding`, so the parse
 * the preview performed to draw the robot is the same parse that seeds the
 * editor. Nothing is fetched for this.
 *
 * Seeding matters because a save replaces the **complete** set. Starting from an
 * empty draft would mean that editing one joint silently cleared every other
 * joint's declaration — with no error and no warning.
 *
 * Every joint that declares one is included, **not** only {@link bindableJoints}.
 * A coupled or fixed joint gets no picker, so a declaration on one is something
 * this UI cannot author — and the extension forbids writing it in the first
 * place — but dropping it here would mean a save silently destroyed it. Not
 * authoring a form is not the same as destroying it.
 */
export function declaredJointBindings(
  joints: readonly Joint[],
): Record<string, JointBinding> {
  const declared: Record<string, JointBinding> = {};
  for (const joint of joints) {
    if (joint.defaultBinding !== undefined) {
      declared[joint.name] = joint.defaultBinding;
    }
  }
  return declared;
}

/**
 * A trailing `[n]` on a message path — `position[3]`.
 *
 * Only a single, non-negative, whole index, and only at the very end. A range
 * (`position[2:5]`) yields several values where a joint needs exactly one, and
 * `valuesAtMessagePath` would hand back the first of them — the same silent
 * wrong answer a bare array path gives. So it is not matched here, and a path
 * carrying one reads as having no index at all: refused rather than truncated.
 */
const TRAILING_INDEX = /^(.*?)\[(\d+)\]$/;

export interface PathAndElement {
  /** The message path with any `[n]` removed — what the field catalogue lists. */
  path: string;
  /** The index written into the path, if it carried one. */
  element?: number;
}

/**
 * Splits `position[3]` into the field the catalogue knows and the element asked
 * for.
 *
 * The index lives **inside the path text** rather than in a control beside it.
 * That spelling is already first-class here — `MessagePathSlice` narrows an
 * array node to one element, `findNodesByParts` copies requested bounds onto
 * whatever matched, and the data server resolves `position[3:4]` to a scalar —
 * so `/joint_states.position[3]` is an ordinary bindable thing and the picker
 * only has to let it be typed. It also keeps the choice *visible in the value*
 * instead of behind a mode nobody re-reads.
 */
export function splitElementIndex(messagePath: string): PathAndElement {
  const match = TRAILING_INDEX.exec(messagePath);
  if (match === null) {
    return { path: messagePath };
  }
  const [, path, index] = match;
  // `position[3]` with nothing before the bracket is not a field at all.
  if (path === "") {
    return { path: messagePath };
  }
  return { path, element: Number.parseInt(index, 10) };
}

/** Why a typed path cannot be written as a declaration, if it cannot. */
export type BindingProblem = "incomplete" | "index_required";

/**
 * Whether a path may be bound as typed.
 *
 * This is where the array trap is stopped. Binding an un-narrowed array path
 * does not fail — the data server takes `valuesAtMessagePath(...)[0]`, so every
 * joint bound to `position` follows element 0 and the robot looks perfectly
 * bound. The three cases are deliberately not one:
 *
 * | The catalogue says | Path has no index | Result |
 * |---|---|---|
 * | array | — | `index_required`: refused, with a message |
 * | scalar | — | fine; there is nothing to index |
 * | nothing (older API, or an unseen field) | — | **accepted**, with the assumption stated on the row |
 * | it has not answered yet (`undefined`) | — | **accepted**; re-judged when it does |
 *
 * The unknown case is accepted rather than blocked because the normal state at
 * ingest time is that no recording of this robot exists yet at all — a URDF is
 * routinely ingested before the robot has been driven. Refusing everything whose
 * type cannot be confirmed would refuse the common case; saying plainly what the
 * binding assumes does not.
 *
 * `undefined` is a fourth case rather than a spelling of `"unknown"`: a
 * catalogue query still in flight has not *reported* an absent type, it has
 * reported nothing at all, and the row's wording about the absence must not run
 * ahead of the answer. See `TopicPathParts.arity`.
 */
export function bindingProblem(
  topic: string,
  messagePath: string,
  arity: FieldArity | undefined,
  element: number | undefined,
): BindingProblem | undefined {
  if (topic === "" || messagePath === "") {
    return "incomplete";
  }
  if (arity === "array" && element === undefined) {
    return "index_required";
  }
  return undefined;
}

export interface BindingDraft {
  topic: string;
  /** The field alone, with no `[n]` — see {@link splitElementIndex}. */
  messagePath: string;
  element?: number;
  /** Omitted when the data's unit is unknown — see {@link makeBinding}. */
  scale?: number;
}

/**
 * One joint's binding, or `undefined` when the draft is not a declaration yet.
 *
 * Both halves are required: a topic with no message path names a message, not a
 * value, and writing it would be writing a binding that cannot resolve.
 *
 * `scale` is written only when something *told* us the unit — the field
 * catalogue's `unit`. Absent means the value is already in the extension's SI
 * units, radians or metres, and that assumption is stated on the joint's row
 * rather than hidden. Guessing a conversion from a field's name is how a robot
 * ends up moving 57 times too far.
 */
export function makeBinding(draft: BindingDraft): JointBinding | undefined {
  if (draft.topic === "" || draft.messagePath === "") {
    return undefined;
  }
  return {
    topic: draft.topic,
    messagePath: draft.messagePath,
    ...(draft.element !== undefined
      ? { element: Math.max(0, Math.trunc(draft.element)) }
      : {}),
    ...(draft.scale !== undefined && draft.scale !== 1
      ? { scale: draft.scale }
      : {}),
  };
}

/**
 * The text a binding reads as in the picker — index included.
 *
 * The inverse of {@link splitElementIndex}, so a saved declaration round-trips
 * back into the one input it was authored in. A `elementNamedBy` binding — which
 * this UI no longer authors, but the format still carries — has no index to
 * write, and shows as the bare path.
 */
export function joinedPathOf(binding: JointBinding | undefined): string {
  if (binding === undefined) {
    return "";
  }
  const index = binding.element === undefined ? "" : `[${binding.element}]`;
  return `${binding.topic}.${binding.messagePath}${index}`;
}

/**
 * A binding for every joint of the robot that can carry one, from one topic.
 *
 * ## Not rendered anywhere today
 *
 * The bulk "apply to all joints" control this backs was cut: authoring is
 * per-joint, through {@link ../JointBindingRow}. It is kept because it is a pure
 * function with its own tests and no UI attached, and because the case it exists
 * for is the common one — a single `/joint_states` driving a whole robot, which
 * is twenty-odd identical selections done by hand.
 *
 * It is also the **only** thing that still writes `elementNamedBy`, and that is
 * worth stating plainly. `sensor_msgs/JointState` correlates `position[i]` with
 * `name[i]` at runtime, per publisher: different controllers publish different
 * subsets in different orders, so an index that is right for one recording can
 * be wrong for the next, while a joint's *name* is right for all of them. The
 * per-joint picker authors a visible index instead — an explicit, checkable
 * choice in place of an invisible correctness property — but the reader and
 * `resolveDefaultBindings` still support the named form, so a declaration
 * written by this function, by a converter, or by hand still resolves.
 *
 * `mimic` joints are skipped, and this is not a detail: a parallel gripper whose
 * two fingers were bound independently would open one and close the other. So
 * are `fixed`, `floating` and `planar` joints, which have no single value.
 */
export function bindAllJoints(
  joints: readonly Joint[],
  topic: string,
  messagePath: string,
  scale?: number,
): Record<string, JointBinding> {
  const bindings: Record<string, JointBinding> = {};
  if (topic === "" || messagePath === "") {
    return bindings;
  }
  for (const joint of bindableJoints(joints)) {
    bindings[joint.name] = {
      topic,
      messagePath,
      elementNamedBy: { path: JOINT_STATE_NAME_PATH, key: joint.name },
      ...(scale !== undefined && scale !== 1 ? { scale } : {}),
    };
  }
  return bindings;
}

/**
 * The unit a joint's values are taken to be in when the binding declares no
 * scale: the extension's own SI units, which follow the joint's type.
 *
 * Shown on the row rather than assumed silently — a topic publishing degrees
 * into a joint read as radians is a robot that looks broken for a reason nobody
 * can see.
 */
export function assumedUnit(joint: Joint): string {
  return joint.type === "prismatic" ? "m" : "rad";
}
